import { jsx, jsxs } from 'react/jsx-runtime';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import 'date-fns-tz';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import '../ui/button.js';
import '@radix-ui/react-progress';
import '@clerk/clerk-react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import '../../lib/toast.js';
import 'react-router-dom';
import { Button } from '../page-layout/Button.js';
import { useApiClient } from '../../lib/api-client.js';
import { useQuery } from '@tanstack/react-query';
import '../page-layout/FormPageLayout.js';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command.js';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover.js';
import './RichTextEditor.js';
import { useDropdownOptions } from '../../hooks/useDropdownOptions.js';
import '@radix-ui/react-switch';
import '@radix-ui/react-dialog';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';
import '../ui/badge.js';
import 'react-day-picker';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import 'react-toastify';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';

function SearchableDropdown({ value, onChange, placeholder = 'Select option', searchPlaceholder = 'Search...', className, endpoint, labelField = 'name', valueField = 'id', emptyText = 'No options found.', filters, disabled = false, side = 'bottom', avoidCollisions = true, }) {
    const apiClient = useApiClient();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [allOptions, setAllOptions] = useState([]);
    // Fetch the currently selected item separately to ensure it always shows in the dropdown
    const { data: selectedItemData } = useQuery({
        queryKey: ['dropdown-selected', endpoint, value],
        queryFn: async () => {
            if (!value)
                return null;
            const response = await apiClient.get(`${endpoint}/${value}`);
            return response?.data ?? null;
        },
        enabled: !!value,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
    // Debounce search input to throttle API calls
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);
    const { options, isLoading, error, hasMore } = useDropdownOptions(endpoint, labelField, valueField, debouncedSearch, page, 25, filters);
    const filtersKey = JSON.stringify(filters);
    // Reset page when search or filters change
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, filtersKey]);
    // Build an option for the currently selected item so it always appears in the list
    const selectedItemOption = selectedItemData
        ? {
            label: String(selectedItemData[labelField] ?? ''),
            value: String(selectedItemData[valueField] ?? ''),
        }
        : null;
    // Accumulate options as we load more pages, ensuring selected value is always included
    useEffect(() => {
        setAllOptions((prev) => {
            // Start with previous options on subsequent pages, fresh options on page 1
            const merged = page === 1 ? [...options] : [...prev, ...options];
            // Prepend the selected item if it's not already in the list
            if (selectedItemOption && value && !merged.some((o) => o.value === value)) {
                merged.unshift(selectedItemOption);
            }
            // Deduplicate by value
            const seen = new Set();
            return merged.filter((o) => {
                if (seen.has(o.value))
                    return false;
                seen.add(o.value);
                return true;
            });
        });
    }, [options, page, value, selectedItemOption?.value]);
    const loadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            setPage(prev => prev + 1);
        }
    }, [hasMore, isLoading]);
    const selectedOption = allOptions.find(option => option.value === value);
    if (error) {
        return jsx("div", { className: "text-sm text-destructive", children: "Failed to load options" });
    }
    return (jsx("div", { className: "block w-full", children: jsxs(Popover, { open: open, onOpenChange: setOpen, children: [jsx(PopoverTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", role: "combobox", "aria-expanded": open, disabled: disabled, className: cn('w-full justify-between', className), children: [selectedOption ? selectedOption.label : placeholder, jsx(ChevronDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })] }) }), jsx(PopoverContent, { className: "w-(--radix-popover-trigger-width) min-w-[300px] p-0", align: "start", side: side, avoidCollisions: avoidCollisions, children: jsxs(Command, { shouldFilter: false, children: [jsx(CommandInput, { placeholder: searchPlaceholder, value: search, onValueChange: setSearch }), jsxs(CommandList, { children: [jsx(CommandEmpty, { children: isLoading ? 'Loading...' : emptyText }), jsxs(CommandGroup, { children: [allOptions.map(option => (jsxs(CommandItem, { value: option.value, onSelect: () => {
                                                    onChange?.(option.value);
                                                    setOpen(false);
                                                }, children: [jsx(Check, { className: cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0') }), option.label] }, option.value))), hasMore && (jsx(CommandItem, { onSelect: loadMore, className: "justify-center text-muted-foreground", disabled: isLoading, children: isLoading ? jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : 'Load more...' }))] })] })] }) })] }) }));
}

export { SearchableDropdown };
//# sourceMappingURL=SearchableDropdown.js.map
